import type { DocumentType } from '@kmdx/contracts';
import { Check, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { CaptureFlow } from '../components/capture-flow';
import { BigButton, Card, Empty, Label, Screen } from '../components/ui';
import { deletePhoto } from '../lib/actions';
import { db, useLive } from '../lib/db';
import { bytes } from '../lib/format';
import { CAPTURABLE_DOCUMENTS, DOCUMENT_LABELS } from '../lib/labels';
import { electronicOrders, isConfirmed } from '../lib/requirements';
import { photoProgressText } from '../lib/sync';
import { useStop, useStops } from '../lib/selectors';
import type { PhotoRecord } from '../lib/types';

/**
 * Заснемане на документите. Без снимка на ЧМР няма фактура — затова списъкът
 * „какво липсва" стои най-отгоре и е написан с думи, не с иконки. Всяка снимка
 * минава през „Ясно ли е?", преди да влезе в опашката; до всяка после пише
 * какво е казал офисът за нея.
 */
export function DocumentsScreen({ stopId }: { stopId: string }) {
  const stop = useStop(stopId);
  const tripStops = useStops(stop?.tripId ?? null);
  const [capturing, setCapturing] = useState<DocumentType | null>(null);

  const photos = useLive(
    async () => {
      const rows = await db.photos.where('orderStopId').equals(stopId).toArray();
      return rows.sort((a, b) => a.capturedAt.localeCompare(b.capturedAt));
    },
    [stopId],
    [],
  );
  const events = useLive(
    async () => (stop?.tripId ? db.events.where('tripId').equals(stop.tripId).toArray() : []),
    [stop?.tripId],
    [],
  );

  if (!stop) {
    return (
      <Screen title="Документи" back>
        <Empty>Този адрес вече не е в курса.</Empty>
      </Screen>
    );
  }

  /** ЧМР на заявка с електронна товарителница не се снима — подписите на телефона са тя. */
  const electronic = stop.orderId ? electronicOrders(tripStops, events).has(stop.orderId) : false;
  const required = stop.requiredDocuments.filter((type) => !(type === 'cmr' && electronic));
  const missing = required.filter((type) => !photos.some((photo) => photo.type === type && isConfirmed(photo)));

  return (
    <Screen title="Документи" back>
      <div className="space-y-4">
        {required.length > 0 ? (
          <Card>
            <Label>Задължително за този адрес</Label>
            <ul className="mt-2 space-y-2">
              {required.map((type) => {
                const done = !missing.includes(type);
                return (
                  <li key={type} className="flex items-center gap-3 text-xl">
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 ${
                        done ? 'border-go bg-go text-white' : 'border-stop text-stop'
                      }`}
                    >
                      {done ? <Check size={22} strokeWidth={3} /> : '!'}
                    </span>
                    <span className={done ? 'line-through opacity-70' : 'font-semibold'}>
                      {DOCUMENT_LABELS[type]}
                    </span>
                  </li>
                );
              })}
            </ul>
            {missing.length === 0 ? (
              <p className="mt-3 text-lg font-semibold text-go">Всичко необходимо е заснето.</p>
            ) : null}
            {electronic && stop.requiredDocuments.includes('cmr') ? (
              <p className="mt-2 text-base text-muted">ЧМР е електронна — не се снима.</p>
            ) : null}
          </Card>
        ) : null}

        <div>
          <Label>Заснеми</Label>
          <div className="mt-2 grid grid-cols-2 gap-3">
            {orderedTypes(required).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setCapturing(type)}
                className="tap flex items-center justify-center gap-2 rounded-2xl border-2 border-line bg-card px-3 py-4 text-lg font-semibold"
              >
                <Plus size={24} />
                {DOCUMENT_LABELS[type]}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label>Заснети ({photos.length})</Label>
          {photos.length === 0 ? (
            <p className="mt-2 text-lg text-muted">Още няма нито една снимка.</p>
          ) : (
            <ul className="mt-2 space-y-3">
              {photos.map((photo) => (
                <PhotoRow key={photo.id} photo={photo} onRetake={() => setCapturing(photo.type)} />
              ))}
            </ul>
          )}
        </div>

        <BigButton tone="plain" onClick={() => window.history.back()}>
          Готово
        </BigButton>
      </div>

      {capturing ? (
        <CaptureFlow
          type={capturing}
          tripId={stop.tripId}
          orderStopId={stop.id}
          onClose={() => setCapturing(null)}
          onDone={() => setCapturing(null)}
        />
      ) : null}
    </Screen>
  );
}

/**
 * Ред за снимка. Неизпратена снимка може да се изтрие или преснеме — тя е само
 * на телефона. Изпратената е и в офиса: там решението е на офиса („приета",
 * „върната"), а изтриване от телефона би излъгало, че я няма.
 */
function PhotoRow({ photo, onRetake }: { photo: PhotoRecord; onRetake: () => void }) {
  const local = photo.status !== 'done';
  const verdict = officeVerdict(photo);
  return (
    <li className="flex items-center gap-3 rounded-2xl border-2 border-line p-2">
      <img src={photo.thumbDataUrl} alt={DOCUMENT_LABELS[photo.type]} className="h-24 w-20 rounded-lg object-cover" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-xl font-semibold">
          {DOCUMENT_LABELS[photo.type]} · стр. {photo.page}
        </div>
        <div className="text-lg text-muted">{bytes(photo.sizeBytes)}</div>
        {verdict ? (
          <div className={`text-lg font-semibold ${verdict.tone}`}>{verdict.text}</div>
        ) : (
          <div
            className={`text-lg font-semibold ${
              photo.status === 'done' ? 'text-go' : photo.status === 'error' ? 'text-stop' : 'text-warn'
            }`}
          >
            {photoProgressText(photo)}
          </div>
        )}
        {photo.status === 'uploading' ? (
          <div className="mt-1 h-2 w-full overflow-hidden rounded bg-line">
            <div className="h-full bg-go transition-[width]" style={{ width: `${Math.round(photo.progress * 100)}%` }} />
          </div>
        ) : null}
      </div>
      {local ? (
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={() => {
              void deletePhoto(photo.id);
              onRetake();
            }}
            className="flex h-14 w-16 items-center justify-center rounded-xl border-2 border-line text-base font-semibold"
          >
            Преснеми
          </button>
          <button
            type="button"
            aria-label="Изтрий снимката"
            onClick={() => {
              if (window.confirm('Да изтрия ли тази снимка?')) void deletePhoto(photo.id);
            }}
            className="flex h-14 w-16 items-center justify-center rounded-xl border-2 border-line text-stop"
          >
            <Trash2 size={26} />
          </button>
        </div>
      ) : null}
    </li>
  );
}

function officeVerdict(photo: PhotoRecord): { text: string; tone: string } | null {
  switch (photo.serverStatus) {
    case 'accepted':
      return { text: 'Приета от офиса', tone: 'text-go' };
    case 'rejected':
      return { text: `Върната${photo.rejectReason ? `: ${photo.rejectReason}` : ''}`, tone: 'text-stop' };
    case 'superseded':
      return { text: 'Заменена с нова снимка', tone: 'text-muted' };
    case 'pending_review':
      return { text: 'Изпратена — чака преглед', tone: 'text-warn' };
    default:
      return null;
  }
}

/** Изискваните документи излизат първи — те са причината екранът да съществува. */
function orderedTypes(required: DocumentType[]): DocumentType[] {
  const rest = CAPTURABLE_DOCUMENTS.filter((type) => !required.includes(type));
  return [...required, ...rest];
}

